import { EmailInvitationPreview as InvitationPreview } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/EmailInvitationPreview';
import { NewLeaverPersonalDataRequirements } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/NewLeaversSteps/NewLeaverPersonalDataRequirements/NewLeaverPersonalDataRequirements';
import { NewLeaversContracts } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/NewLeaversSteps/NewLeaversContracts/NewLeaversContracts';
import {
	INVITATION_PREVIEW_INPUTS_FORM,
	useNewLeaversInvitationPreviewController as useLeaverInvitationPreviewController,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/NewLeaversSteps/NewLeaversInvitationPreview/useNewLeaversInvitationPreviewController';
import { NewLeaversSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/NewLeaversSteps/NewLeaversSummary/NewLeaversSummary';
import {
	MAX_STEP_NUMBER,
	useNewLeaversModalController,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/useNewLeaversModalController';
import { IndividualSchedule } from '@dealstack/frontend/shared/components/IndividualSchedule/IndividualSchedule';
import { DynamicContinueButton } from '@dealstack/frontend/shared/components/DynamicContinueButton/DynamicContinueButton';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { ButtonsApart } from '@dealstack/frontend/shared/components/fundament/Button/ButtonLayouts';
import { BackButton } from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Divider } from '@dealstack/frontend/shared/components/fundament/Divider/Divider';
import { Modal } from '@dealstack/frontend/shared/components/fundament/Modal/Modal';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { createContext, useMemo } from 'react';
import styled from 'styled-components';

import { NewLeaversIntroduction } from './NewLeaversSteps/NewLeaversIntroduction/NewLeaversIntroduction';
import { NewLeaversInvestors } from './NewLeaversSteps/NewLeaversInvestors/NewLeaversInvestors';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

interface Props {
	className?: string;
}

export const NewLeaversModalContext = createContext<
	ReturnType<typeof useNewLeaversModalController>
>({} as ReturnType<typeof useNewLeaversModalController>);

const NewLeaversModal = ({ className }: Props) => {
	const tMipManage = useTranslationMipManage();
	const newLeaversModalController = useNewLeaversModalController();
	const {
		currentStep,
		mipData,
		setShowNewLeaversModal,
		onPreviousStepClicked,
		onNextStepClicked,
		inviteTemplateForm,
		disableContinue,
		totalStepsToDisplay,
		currentStepToDisplay,
	} = newLeaversModalController;

	const { invitationPreviewForm } = useLeaverInvitationPreviewController({
		inviteTemplateForm,
	});

	const previewText = invitationPreviewForm.watch(INVITATION_PREVIEW_INPUTS_FORM.EMAIL_BODY_TEXT);

	const getCurrentStepComponent = useMemo(() => {
		switch (currentStep) {
			case 0:
				return <NewLeaversIntroduction />;
			case 1:
				return <NewLeaverPersonalDataRequirements />;
			case 2:
				return <NewLeaversContracts />;
			case 3:
				return <IndividualSchedule />;
			case 4:
				return (
					<InvitationPreview
						className={className}
						form={invitationPreviewForm}
						inputName={INVITATION_PREVIEW_INPUTS_FORM.EMAIL_BODY_TEXT}
						bodyText={previewText}
						previewTitle={tMipManage('new_leaver.preview.title')}
						emailPreviewTitle={tMipManage('new_leaver.preview.email.title')}
						emailPreviewChild={
							<div>
								<Button size="medium" variant="secondary">
									{tMipManage('new_leaver.preview.email.button')}
								</Button>
							</div>
						}
					/>
				);
			case 5:
				return <NewLeaversInvestors />;
		}
		return;
	}, [currentStep, previewText]);

	const isFinalStep = currentStep === MAX_STEP_NUMBER;
	return (
		<NewLeaversModalContext.Provider value={newLeaversModalController}>
			{isFinalStep ? (
				<NewLeaversSummary />
			) : (
				<Modal
					size={isFinalStep ? 'sm' : 'lg'}
					reactModalProps={{
						shouldCloseOnOverlayClick: false,
					}}
					onClose={() => {
						setShowNewLeaversModal(false);
					}}
					className={className}
				>
					<>
						<SectionHeader
							title={tMipManage('investors.new_leavers_modal.new_leavers')}
							overtitle={mipData?.entity?.displayName}
							size="h4b"
						/>
						<ContentWrapper>{getCurrentStepComponent}</ContentWrapper>
						<FooterWrapper>
							<Divider />
							<ButtonsApart>
								<BackButton
									disabled={currentStep === 0}
									onClick={onPreviousStepClicked}
								/>
								<Text
									colorFaded
									variant="p-sm"
									data-testid={TEST_ELEMENT_IDS.MODAL_PROGRESS_TEXT}
								>
									{tMipManage('investors.upsert_investor_shares_modal.progress', {
										currentStep: currentStepToDisplay,
										maxSteps: totalStepsToDisplay,
									})}
								</Text>
								<DynamicContinueButton
									disabled={currentStep === MAX_STEP_NUMBER || disableContinue}
									onClick={onNextStepClicked}
								/>
							</ButtonsApart>
						</FooterWrapper>
					</>
				</Modal>
			)}
		</NewLeaversModalContext.Provider>
	);
};

const FooterWrapper = styled.div`
	${Divider} {
		margin: 2em 0 1em 0;
	}

	margin-top: auto;
`;
const ContentWrapper = styled.div`
	position: relative;
`;

const Styled = styled(NewLeaversModal)`
	${IndividualSchedule} {
		margin: 1em 0 3em 0;
	}
`;

export { Styled as NewLeaversModal };
